/**
 * Comparison Detail Page - Individual comparison page
 * /compare/:slug
 */
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import SEO from '@/components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Scale,
  ArrowLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Briefcase,
  GraduationCap,
  Globe,
  Plane,
  Award,
  HelpCircle,
  Loader2,
  Users,
  Target
} from 'lucide-react';

const API_URL = window.location.origin || '';

const ComparisonDetailPage = () => {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [relatedComparisons, setRelatedComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/comparisons/public/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setComparison(data);

          // Fetch related comparisons
          const relatedRes = await fetch(`${API_URL}/api/comparisons/public`);
          if (relatedRes.ok) {
            const allComps = await relatedRes.json();
            const related = allComps
              .filter(c => c.slug !== slug && c.comparison_type === data.comparison_type)
              .slice(0, 3);
            setRelatedComparisons(related);
          }
        } else {
          navigate('/compare');
          return;
        }
      } catch (error) {
        console.error('Error fetching comparison:', error);
        navigate('/compare');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!comparison) {
    return null;
  }

  const items = comparison.items || [];

  // Color palette for comparison items
  const itemColors = [
    { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-500' },
    { bg: 'bg-green-500', light: 'bg-green-50', text: 'text-green-600', border: 'border-green-500' },
    { bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-500' },
    { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-500' },
  ];

  return (
    <>
      <SEO 
        title={comparison.title}
        description={comparison.description}
        keywords={comparison.keywords}
        canonical={`https://unixperts.com/compare/${slug}`}
      />
      
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        {/* Hero Section */}
        <section className={`py-12 md:py-16 ${isDark ? 'bg-gradient-to-br from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-br from-purple-600 to-blue-600'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link
              to="/compare"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
              data-testid="back-to-comparisons"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Comparisons
            </Link>

            <div className="max-w-4xl">
              <Badge className="bg-white/20 text-white mb-4">
                <Scale className="w-3 h-3 mr-1" />
                {comparison.comparison_type === 'destination' ? 'Destination Comparison' : 
                 comparison.comparison_type === 'program' ? 'Program Comparison' : 'Degree Comparison'}
              </Badge>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 font-display" data-testid="comparison-title">
                {comparison.title}
              </h1>
              <p className="text-xl text-white/80 mb-6">
                {comparison.tagline}
              </p>

              {/* Quick comparison chips */}
              <div className="flex flex-wrap gap-3">
                {items.map((item, idx) => (
                  <Badge 
                    key={idx}
                    className={`text-base py-2 px-4 ${itemColors[idx % itemColors.length].bg} text-white`}
                  >
                    {item.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
              <CardContent className="p-6">
                <p className={`text-lg leading-relaxed ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                  {comparison.introduction}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Quick Comparison Table */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Quick Comparison
            </h2>
            <Card className={`overflow-hidden ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? 'border-white/10' : ''}>
                      <TableHead className={`w-[150px] ${isDark ? 'text-white/70' : ''}`}>Factor</TableHead>
                      {items.map((item, idx) => (
                        <TableHead 
                          key={idx}
                          className={`text-center ${itemColors[idx % itemColors.length].text} font-bold`}
                        >
                          {item.name}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className={isDark ? 'border-white/10' : ''}>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          Duration
                        </div>
                      </TableCell>
                      {items.map((item, idx) => (
                        <TableCell key={idx} className={`text-center ${isDark ? 'text-white/80' : ''}`}>
                          {item.duration}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className={isDark ? 'border-white/10' : ''}>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          Cost
                        </div>
                      </TableCell>
                      {items.map((item, idx) => (
                        <TableCell key={idx} className={`text-center ${isDark ? 'text-white/80' : ''}`}>
                          {item.avg_cost}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className={isDark ? 'border-white/10' : ''}>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                        <div className="flex items-center gap-2">
                          <Plane className="w-4 h-4 text-slate-400" />
                          Work Visa
                        </div>
                      </TableCell>
                      {items.map((item, idx) => (
                        <TableCell key={idx} className={`text-center ${isDark ? 'text-white/80' : ''}`}>
                          {item.work_visa}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className={isDark ? 'border-white/10' : ''}>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-slate-400" />
                          PR Pathway
                        </div>
                      </TableCell>
                      {items.map((item, idx) => (
                        <TableCell key={idx} className={`text-center ${isDark ? 'text-white/80' : ''}`}>
                          {item.pr_pathway}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className={isDark ? 'border-white/10' : ''}>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          Avg Salary
                        </div>
                      </TableCell>
                      {items.map((item, idx) => (
                        <TableCell key={idx} className={`text-center font-semibold ${itemColors[idx % itemColors.length].text}`}>
                          {item.avg_salary}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </section>

        {/* Detailed Comparison Cards */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Detailed Analysis
            </h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {items.map((item, idx) => (
                <Card 
                  key={idx}
                  className={`overflow-hidden ${isDark ? 'bg-white/5 border-white/10' : ''}`}
                >
                  <div className={`h-2 ${itemColors[idx % itemColors.length].bg}`} />
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-3 ${isDark ? 'text-white' : ''}`}>
                      <div className={`w-10 h-10 rounded-full ${itemColors[idx % itemColors.length].bg} text-white flex items-center justify-center font-bold`}>
                        {idx + 1}
                      </div>
                      {item.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Top Universities */}
                    {item.top_universities?.length > 0 && (
                      <div>
                        <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          <GraduationCap className="w-4 h-4" />
                          Top Universities
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {item.top_universities.map((uni, i) => (
                            <Badge key={i} variant="secondary" className={isDark ? 'bg-white/10 text-white/80' : ''}>
                              {uni}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pros */}
                    {item.pros?.length > 0 && (
                      <div>
                        <h4 className={`font-semibold mb-2 text-green-600 flex items-center gap-2`}>
                          <CheckCircle2 className="w-4 h-4" />
                          Advantages
                        </h4>
                        <ul className="space-y-2">
                          {item.pros.map((pro, i) => (
                            <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Cons */}
                    {item.cons?.length > 0 && (
                      <div>
                        <h4 className={`font-semibold mb-2 text-red-500 flex items-center gap-2`}>
                          <XCircle className="w-4 h-4" />
                          Disadvantages
                        </h4>
                        <ul className="space-y-2">
                          {item.cons.map((con, i) => (
                            <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                              <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Best For */}
                    {item.best_for && (
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : itemColors[idx % itemColors.length].light}`}>
                        <h4 className={`font-semibold mb-1 flex items-center gap-2 ${itemColors[idx % itemColors.length].text}`}>
                          <Target className="w-4 h-4" />
                          Best For
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          {item.best_for}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Verdict */}
        {comparison.verdict && (
          <section className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Card className={`overflow-hidden ${isDark ? 'bg-gradient-to-br from-purple-500/20 to-blue-500/20 border-white/20' : 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200'}`}>
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Award className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Our Verdict
                    </h2>
                  </div>
                  <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}>
                    <p className={`text-lg leading-relaxed ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                      {comparison.verdict.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i} className={isDark ? 'text-purple-300' : 'text-purple-700'}>{part}</strong> : part
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* FAQ */}
        {comparison.faq?.length > 0 && (
          <section className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <HelpCircle className="w-6 h-6" />
                Frequently Asked Questions
              </h2>
              <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
                <CardContent className="p-6">
                  <Accordion type="single" collapsible className="w-full">
                    {comparison.faq.map((item, idx) => (
                      <AccordionItem key={idx} value={`faq-${idx}`} className={isDark ? 'border-white/10' : ''}>
                        <AccordionTrigger className={`text-left ${isDark ? 'text-white hover:text-purple-300' : 'hover:text-purple-600'}`}>
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className={isDark ? 'text-white/70' : 'text-slate-600'}>
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className={`overflow-hidden ${isDark ? 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 border-white/20' : 'bg-gradient-to-r from-purple-600 to-blue-600'}`}>
              <CardContent className="p-8 text-center text-white">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-90" />
                <h3 className="text-2xl font-bold mb-2">Need Personalized Advice?</h3>
                <p className={`mb-6 ${isDark ? 'text-white/80' : 'text-white/90'}`}>
                  Our expert counselors can help you make the right choice based on your unique profile and goals.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    className="bg-white text-purple-600 hover:bg-white/90"
                    asChild
                  >
                    <Link to="/contact">
                      Book Free Consultation
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="hero-outline"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chat'))}
                    data-testid="talk-ai-counselor-btn"
                  >
                    Talk to AI Counselor
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Related Comparisons */}
        {relatedComparisons.length > 0 && (
          <section className={`py-12 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Related Comparisons
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedComparisons.map(comp => (
                  <Link
                    key={comp.id}
                    to={`/compare/${comp.slug}`}
                    className="group"
                  >
                    <Card className={`h-full transition-all duration-300 hover:shadow-lg ${
                      isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white hover:shadow-xl'
                    }`}>
                      <CardContent className="p-6">
                        <h3 className={`font-bold mb-2 group-hover:text-purple-500 transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {comp.title}
                        </h3>
                        <p className={`text-sm line-clamp-2 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                          {comp.tagline}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default ComparisonDetailPage;
